import React, {
  useCallback,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import {
  EncounterSummaryViewModel,
  ServiceFacilityDTO,
  UserDTO,
} from 'dtos';
import IconButton from 'core/components/IconButton';
import Skeleton from 'core/components/Skeleton';
import Label from 'pages/Dashboard/components/Label';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  PCCEncounterValue,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import {
  useGetVisitNoteRelatedInfo,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import RTMEncounterSelect from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMEncounterSelect';
import RTMVisitLabel from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/RTM/RTMVisitLabel';
import DoctorSelectPopover from 'views/EMR/components/DoctorSelectPopover';

type Props = {
  patientId: number;
  visitNoteId?: number;
  value: PCCEncounterValue | null;
  disabled: boolean;
  onChange: (value: PCCEncounterValue | null) => void;
};

export default function RTMEncounterSelection({
  patientId,
  visitNoteId,
  value,
  disabled,
  onChange,
}: Props) {
  const encounterSelectWrapperRef = useRef<HTMLDivElement>(null);
  const [doctorPopoverAnchor, setDoctorPopoverAnchor] = useState<HTMLElement | null>(null);
  const [pendingDoctor, setPendingDoctor] = useState<UserDTO | null>(null);
  const [pendingFacility, setPendingFacility] = useState<ServiceFacilityDTO | null>(null);

  const {
    data: relatedInfo,
    isLoading: isLoadingRelatedInfo,
  } = useGetVisitNoteRelatedInfo(disabled ? (visitNoteId ?? 0) : 0);

  const selectedEncounterId = !isNil(value?.encounterId)
    ? value?.encounterId ?? undefined
    : undefined;
  const isNewEncounter = !isNil(value?.newAppointment);

  const handleEncounterChange = useCallback((encounter: EncounterSummaryViewModel) => {
    if (!disabled) {
      if (encounter?.encounterId === 0) {
        setDoctorPopoverAnchor(encounterSelectWrapperRef.current);
      } else if (!isNil(encounter?.encounterId) && encounter.encounterId > 0) {
          setPendingDoctor(null);
          setPendingFacility(null);
          onChange({
            encounterId: encounter.encounterId,
          });
      }
    };
  }, [disabled, onChange]);

  const handleClearNewEncounter = useCallback(() => {
    if (!disabled) {
      setPendingDoctor(null);
      setPendingFacility(null);
      onChange(null);
    }
  }, [disabled, onChange]);

  return (
    <>
      {disabled && (
        <Wrapper
          id="rtm-encounter"
          label="Encounter"
          labelClasses="font-semibold text-xs text-gray-500"
        >
          {isLoadingRelatedInfo || isNil(relatedInfo) ? (
            <Skeleton className="!h-6 !w-full" variant="rectangular" />
          ) : (
            <div className="w-full border border-default-input bg-gray-50 rounded-default-input pl-3 py-[7px] text-xs text-primary">
              <RTMVisitLabel
                visit={{
                  typeOfVisitDescription: relatedInfo.typeOfVisitDescription,
                  startDateTime: relatedInfo.visitDateTime,
                }}
              />
            </div>
          )}
        </Wrapper>
      )}

      {!disabled && !isNewEncounter && (
        <div ref={encounterSelectWrapperRef}>
          <RTMEncounterSelect
            patientId={patientId}
            value={selectedEncounterId}
            onChange={handleEncounterChange}
          />
        </div>
      )}

      {!disabled && isNewEncounter && (
        <div className="flex flex-col gap-1">
          <Label
            required
            id="rtm-encounter"
            label="Encounter"
          />
          <div
            role="button"
            tabIndex={-1}
            onKeyDown={noop}
            ref={encounterSelectWrapperRef}
            onClick={() => {
              setDoctorPopoverAnchor(encounterSelectWrapperRef.current);
            }}
            className="transition-colors flex w-full p-2 border rounded border-gray-300 text-xs justify-between cursor-pointer hover:border-blue-500"
          >
            <div className="flex gap-1 flex-1 flex-col">
              <div className="font-semibold">
                {getDisplayName(pendingDoctor?.formattedNames) || 'New encounter'}
              </div>
              <div>{pendingFacility?.nickname ?? 'Provider and facility selected'}</div>
            </div>
            <IconButton
              size="small"
              tooltip="Remove"
              onClick={(event) => {
                event.stopPropagation();
                handleClearNewEncounter();
              }}
              className="!h-6"
              color="error"
            >
              <CloseIcon className="!w-5 !h-5 !text-gray-600" />
            </IconButton>
          </div>
        </div>
      )}

      {!isNil(doctorPopoverAnchor) && !disabled && (
        <DoctorSelectPopover
          anchorElement={doctorPopoverAnchor}
          value={{
            doctor: pendingDoctor,
            serviceFacility: pendingFacility,
          }}
          onClose={() => { setDoctorPopoverAnchor(null); }}
          onConfirm={(nextValue) => {
            const doctor = nextValue?.doctor ?? null;
            const serviceFacility = nextValue?.serviceFacility ?? null;
            setPendingDoctor(doctor);
            setPendingFacility(serviceFacility);
            if (!isNil(doctor?.userId) && !isNil(serviceFacility?.serviceFacilityId)) {
              onChange({
                newAppointment: {
                  userId: doctor.userId,
                  serviceFacilityId: serviceFacility.serviceFacilityId,
                },
              });
            }
            setDoctorPopoverAnchor(null);
          }}
        />
      )}
    </>
  );
}
